import React, { ElementType, forwardRef, ReactElement, ReactNode } from "react";

import classNames from "classnames";

import {
  StyledCommandPaletteCommandItem,
  StyledIcon,
} from "./CommandPalette.styles";
import { CommandPaletteItemProps } from "./CommandPaletteItem";
import { useSelectedContext } from "./context";
import {
  PolymorphicComponentPropsWithRef,
  PolymorphicRef,
} from "../../utils/types";
import { ArrowRightIcon } from "../Icons";
import { Typography } from "../Typography/Typography";

export interface CommandPaletteCommandItemProps
  extends CommandPaletteItemProps {
  title: string;
  renderIcon: () => ReactNode;
}

export type PolymorphicCommandPaletteCommandItemProps<C extends ElementType> =
  PolymorphicComponentPropsWithRef<C, CommandPaletteCommandItemProps>;

export type CommandPaletteComponent = <
  C extends ElementType = typeof CommandPaletteCommandItem
>(
  props: PolymorphicCommandPaletteCommandItemProps<C>
) => ReactElement | ReactNode | null;

export const CommandPaletteCommandItem: CommandPaletteComponent & {
  displayName?: string;
} = forwardRef(
  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-ignore - due to @node/types making ts more strict
  <C extends ElementType = typeof CommandPaletteCommandItem>(
    {
      title,
      renderIcon,
      className,
      index,
      as,
      ...rest
    }: PolymorphicCommandPaletteCommandItemProps<C>,
    ref?: PolymorphicRef<C>
  ) => {
    const Component = as || "a";
    const icon = renderIcon();
    const { selected, setSelected } = useSelectedContext();

    const onMouseEnter = () => {
      setSelected(index as number);
    };

    return (
      <li>
        <StyledCommandPaletteCommandItem
          {...rest}
          as={Component}
          ref={ref}
          className={classNames("cp-item cp-command", className)}
          tabIndex={1}
          aria-selected={selected === index}
          onMouseEnter={onMouseEnter}
          $selected={index === selected}
        >
          <StyledIcon>{icon}</StyledIcon>
          <Typography
            overflow="truncate"
            whitespace="no-wrap"
            variant="Body 1"
            color="primary"
            css={`
              flex: 1;
            `}
          >
            {title}
          </Typography>
          <ArrowRightIcon />
        </StyledCommandPaletteCommandItem>
      </li>
    );
  }
);

CommandPaletteCommandItem.displayName = "CommandPaletteCommandItem";
